import checker from '@/sui/tools/checker'

const debug = process.env.VUE_APP_SPIADI_DEBUG === '1'

function percentile (arr, p) {
  if (arr.length === 0) {
    return 0
  }

  if (typeof p !== 'number') {
    throw new TypeError('p must be a number')
  }

  if (p <= 0) {
    return arr[0]
  }
  if (p >= 1) {
    return arr[arr.length - 1]
  }

  const index = (arr.length - 1) * p
  const lower = Math.floor(index)
  const upper = lower + 1
  const weight = index % 1

  if (upper >= arr.length) {
    return arr[lower]
  }

  return arr[lower] * (1 - weight) + arr[upper] * weight
}

function median (v) {
  return percentile(v, 0.5)
}

function mean (arr) {
  let sum = 0
  arr.forEach(v => { sum += v })
  return sum / arr.length
}

function variance (arr, mean) {
  const len = arr.length
  if (len <= 1) {
    return 0
  }

  let sum = 0
  arr.forEach(v => { sum += (v - mean) * (v - mean) })
  return sum / (arr.length - 1)
}

function countValuesAbove (list, value) {
  const count = list.length
  let i = count - 1
  while (i >= 0 && list[i] > value) {
    i--
  }
  return count - 1 - i
}

function countValuesBelow (list, value) {
  const count = list.length
  let i = 0
  while (i < count && list[i] < value) {
    i++
  }
  return i
}

if (debug) {
  const c = checker()
  const list = [1, 2, 3, 4, 5]
  c.check(countValuesAbove(list, 6) === 0, 'No value above the limit')
  c.check(countValuesAbove(list, 0) === 5, 'All values above the limit')
  c.check(countValuesAbove(list, 3) === 2, 'A few values above the limit')

  c.check(countValuesBelow(list, 6) === 5, 'All values below the limit')
  c.check(countValuesBelow(list, 0) === 0, 'No value below the limit')
  c.check(countValuesBelow(list, 3) === 2, 'A few values below the limit')
  c.end()
}

function quality (oCount, totalCount) {
  if (totalCount === 1) {
    return 1
  }
  return (oCount / totalCount) > 0.2 ? 2 : 0
}

function getRepTestStat (array) {
  if (array == null) {
    return { err: 'Test statistique impossible : tableau de donnée vide' }
  }

  const countLines = array.length
  if (countLines < 2) {
    return { err: 'Test statistique impossible : lignes insuffisantes' }
  }

  let totalValues = 0
  const sumValuesInLines = []
  const sumValuesInCols = {}

  for (let i = 0; i < countLines; i++) {
    const line = array[i]
    const countColumns = line.length
    if (countColumns < 2) {
      return { err: 'Test statistique impossible : colonnes insuffisantes' }
    }

    let totalValuesInLine = 0
    for (let j = 0; j < line.length; j++) {
      const value = line[j]
      const sumCol = sumValuesInCols[j]
      if (sumCol == null) {
        sumValuesInCols[j] = value
      } else {
        sumValuesInCols[j] = sumCol + value
      }
      totalValuesInLine += value
    }
    totalValues += totalValuesInLine
    sumValuesInLines.push(totalValuesInLine)
  }

  for (let i = 0; i < array.length; i++) {
    const line = array[i]
    const sumLine = sumValuesInLines[i]

    for (let j = 0; j < line.length; j++) {
      const sumCol = sumValuesInCols[j]
      const v = (sumCol * sumLine) / totalValues
      if (v < 5) {
        return { type: 'fisher', name: 'Test exact de Fisher' }
      }
    }
  }

  return { type: 'chisquare', name: 'χ²' }
}

function getMmTestStat (array) {
  if (array == null) {
    return { err: 'Test statistique impossible : tableau de donnée vide' }
  }

  const countColumns = array.length
  if (countColumns < 2) {
    return { err: 'Test statistique impossible : colonnes insuffisantes' }
  }

  if (countColumns > 2) {
    return { type: 'kruskal', name: 'Kruskal-Wallis' }
  }

  for (let i = 0; i < countColumns; i++) {
    const columnValues = array[i]
    if (columnValues.length < 50) {
      return { type: 'mann', name: 'Wilcoxon - Mann Whitney' }
    }
  }

  return { type: 'student', name: 'T-test (variances inégales)' }
}

function statistics (p) {
  const result = {
    median: 0,
    mean: 0,
    p25: 0,
    p75: 0,
    min: 0,
    max: 0,
    inf: 0,
    sup: 0,
    d1: 0,
    d9: 0,
    n: 0,
    sd: null,
    nOutInf: 0,
    nOutSup: 0,
    nOutTotal: 0,
    quality: null
  }

  if (p.length === 0) {
    return result
  }

  const s = p.slice().sort((a, b) => a - b)
  result.n = s.length
  result.median = median(s)
  result.p25 = percentile(s, 0.25)
  result.p75 = percentile(s, 0.75)
  result.d1 = percentile(s, 0.1)
  result.d9 = percentile(s, 0.9)
  result.min = s[0]
  result.max = s[s.length - 1]
  result.mean = mean(s)

  const iq = (result.p75 - result.p25) * 1.5
  result.inf = result.p25 - iq
  result.sup = result.p75 + iq

  result.sd = null
  if (s.length > 1) {
    result.sd = Math.sqrt(variance(s, result.mean))
  }

  result.nOutInf = countValuesBelow(s, result.inf)
  result.nOutSup = countValuesAbove(s, result.sup)
  result.nOutTotal = result.nOutInf + result.nOutSup
  result.quality = quality(result.nOutTotal, result.n)
  return result
}

function countDigits (n, decimalSign = '.') {
  const stringN = `${n}`
  const lenN = stringN.length
  const result = {
    integers: 0,
    decimals: 0
  }

  if (n == null || lenN === 0) {
    return result
  }

  const commaPosition = stringN.indexOf(decimalSign)
  if (commaPosition === -1) {
    result.integers = lenN
    return result
  }

  const countDecimals = lenN - commaPosition - 1
  result.decimals = countDecimals
  result.integers = commaPosition
  return result
}

export default {
  median,
  statistics,
  quality,
  getTestStat: {
    rep: getRepTestStat,
    mm: getMmTestStat
  },
  countDigits
}
